import { DiffedField, ParsedEntryValue } from '../utils/formUtils';
import { Variable } from '../types';
import { Button } from '../../components/Button';
import { Dialog } from '../component/Dialog';
import { Diffs } from '../smart-components/Diffs';
import { useMemo } from 'react';
import { logWithDetails } from 'sentry';

export type ConfirmDiffedFieldsModalData = {
	diffedFields: Record<string, DiffedField>;
	dirtyFields: Record<string, ParsedEntryValue>;
};

export const ConfirmDiffModal = ({
	onClose,
	diff,
	onConfirm,
	onDelete,
	submitting,
	variables,
	title
}: {
	onClose: () => void;
	diff?: Record<string, DiffedField>;
	onConfirm?: () => void;
	submitting?: boolean;
	variables: Variable[];
	onDelete: () => void;
	title: string;
}) => {
	const diffs = useMemo(() => {
		if (!diff) return [];

		const ret: Parameters<typeof Diffs>[0]['diffs'] = [];

		for (const [key, { from, to }] of Object.entries(diff)) {
			let variable = variables.find(variable => variable.variableName === key);

			if (!variable) {
				logWithDetails(new Error('Variable not found'), { key });

				variable = {
					// This is not supposed to happen, providing a type safe solution so we dont have to make variable optional all the way through
					variableName: key,
					variableType: 'string',
					description: '',
					obligatory: false,
					variableLabel: key
				};
			}

			ret.push({
				from,
				to,
				variable
			});
		}

		return ret;
	}, [diff, variables]);

	if (!diff) return null;

	return (
		<Dialog open onClose={onClose} title={title}>
			<div className="flex flex-col w-[600px] h-[720px]">
				<div className="gap-6 overflow-y-auto grow">
					<Diffs diffs={diffs} />
				</div>

				<div className="flex justify-between mt-10">
					{onDelete && (
						<Button text="Discard changes" variant="destructive" onClick={onDelete} />
					)}

					<div className="flex gap-2">
						<Button text="Continue editing" onClick={onClose} variant="secondary" />

						{onConfirm && (
							<Button text="Save changes" onClick={onConfirm} loading={submitting} />
						)}
					</div>
				</div>
			</div>
		</Dialog>
	);
};
